import React from 'react';
import {
  ChartBarIcon,
  UserGroupIcon,
  FolderIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface SimpleChartsProps {
  analytics: any;
  className?: string;
}

const SimpleCharts: React.FC<SimpleChartsProps> = ({ analytics, className = '' }) => {
  if (!analytics) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'TODO': return 'bg-gray-100 text-gray-800';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800';
      case 'REVIEW': return 'bg-yellow-100 text-yellow-800';
      case 'DONE': return 'bg-green-100 text-green-800';
      case 'PLANNING': return 'bg-purple-100 text-purple-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'ON_HOLD': return 'bg-orange-100 text-orange-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'HIGH': return <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />;
      case 'NORMAL': return <ClockIcon className="w-4 h-4 text-blue-500" />;
      case 'LOW': return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
      default: return <ClockIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const statusData = analytics.distributions?.tasksByStatus || [];
  const priorityData = analytics.distributions?.tasksByPriority || [];
  const teamPerformanceData = analytics.performance?.teamPerformance || [];

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-lg text-white">
          <div className="flex items-center">
            <FolderIcon className="w-8 h-8 opacity-80" />
            <div className="ml-3">
              <p className="text-sm font-medium opacity-90">Total Projects</p>
              <p className="text-2xl font-bold">{analytics.overview?.totalProjects || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-lg text-white">
          <div className="flex items-center">
            <ClockIcon className="w-8 h-8 opacity-80" />
            <div className="ml-3">
              <p className="text-sm font-medium opacity-90">Total Tasks</p>
              <p className="text-2xl font-bold">{analytics.overview?.totalTasks || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-lg text-white">
          <div className="flex items-center">
            <UserGroupIcon className="w-8 h-8 opacity-80" />
            <div className="ml-3">
              <p className="text-sm font-medium opacity-90">Total Teams</p>
              <p className="text-2xl font-bold">{analytics.overview?.totalTeams || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 rounded-lg text-white">
          <div className="flex items-center">
            <ChartBarIcon className="w-8 h-8 opacity-80" />
            <div className="ml-3">
              <p className="text-sm font-medium opacity-90">Total Users</p>
              <p className="text-2xl font-bold">{analytics.overview?.totalUsers || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      {statusData.length > 0 && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Task Status Distribution</h3>
          <div className="space-y-4">
            {statusData.map((item: any, index: number) => {
              const total = statusData.reduce((sum: number, s: any) => sum + s.count, 0);
              const percentage = total > 0 ? (item.count / total) * 100 : 0;
              
              return (
                <div key={item.status} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(item.status)}`}>
                      {item.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-12 text-right">
                      {item.count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Priority Distribution */}
      {priorityData.length > 0 && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Task Priority Distribution</h3>
          <div className="space-y-4">
            {priorityData.map((item: any) => {
              const total = priorityData.reduce((sum: number, p: any) => sum + p.count, 0);
              const percentage = total > 0 ? (item.count / total) * 100 : 0;
              
              return (
                <div key={item.priority} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getPriorityIcon(item.priority)}
                    <span className="text-sm font-medium text-gray-700">{item.priority}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          item.priority === 'HIGH' ? 'bg-red-500' :
                          item.priority === 'NORMAL' ? 'bg-blue-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-12 text-right">
                      {item.count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Team Performance */}
      {teamPerformanceData.length > 0 && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Team Performance</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teamPerformanceData.map((team: any) => (
              <div key={team.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3 mb-3">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: team.color || '#6B7280' }}
                  />
                  <h4 className="font-medium text-gray-900">{team.name}</h4>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">Task Completion</span>
                      <span className="font-medium">{team.taskCompletionRate?.toFixed(1) || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${team.taskCompletionRate || 0}%` }}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">Project Completion</span>
                      <span className="font-medium">{team.projectCompletionRate?.toFixed(1) || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${team.projectCompletionRate || 0}%` }}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-xs text-gray-600 mt-3">
                    <div className="text-center">
                      <div className="font-medium text-gray-900">{team._count?.projects || 0}</div>
                      <div>Projects</div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-gray-900">{team._count?.tasks || 0}</div>
                      <div>Tasks</div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-gray-900">{team._count?.members || 0}</div>
                      <div>Members</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {analytics.trends?.recentActivity && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Projects */}
          {analytics.trends.recentActivity.recentProjects && (
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Projects</h3>
              <div className="space-y-3">
                {analytics.trends.recentActivity.recentProjects.slice(0, 5).map((project: any) => (
                  <div key={project.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{project.name}</p>
                      <p className="text-sm text-gray-500">
                        by {project.creator.firstName} {project.creator.lastName}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(project.status)}`}>
                      {project.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Tasks */}
          {analytics.trends.recentActivity.recentTasks && (
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Tasks</h3>
              <div className="space-y-3">
                {analytics.trends.recentActivity.recentTasks.slice(0, 5).map((task: any) => (
                  <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{task.title}</p>
                      <p className="text-sm text-gray-500">
                        {task.project ? (
                          <span className="flex items-center space-x-1">
                            <div 
                              className="w-2 h-2 rounded-full" 
                              style={{ backgroundColor: task.project.color || '#6B7280' }}
                            />
                            <span>{task.project.name}</span>
                          </span>
                        ) : (
                          'No Project'
                        )}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                      {task.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SimpleCharts;
